import React from 'react';
import { Block, Inline } from '@contentful/rich-text-types';
import type { Sys } from 'src/types/contentful-sdk-compat';
import { SpringValue } from '@react-spring/web';
import type { ITheme } from 'threads5/themes/theme';
import SxProps from 'threads5/themes/sx-props';

import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import { Form, Label, Slider } from './ACHCalculator.styled';
import { VisuallyHiddenContainer } from 'src/components-v2/DataDisplay/VisuallyHiddenContainer';
import { formatCurrency } from 'src/lib/utils';
import type { IUserInput } from './ACHCalculator.types';

interface ACHFormProps {
  achAdoptionInputRef: React.RefObject<HTMLInputElement>;
  achAdoptionSliderRef: React.RefObject<HTMLInputElement>;
  averagePaymentSizeInputRef: React.RefObject<HTMLInputElement>;
  averagePaymentSizeSliderRef: React.RefObject<HTMLInputElement>;
  fields: {
    formBody?: Block | Inline;
    trackingContext?: Record<string, unknown>;
    styles?: SxProps<ITheme>;
  };
  formRef: React.RefObject<HTMLFormElement>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasUserInteracted: React.MutableRefObject<boolean>;
  monthlyVolumeInputRef: React.RefObject<HTMLInputElement>;
  monthlyVolumeSliderRef: React.RefObject<HTMLInputElement>;
  sys: Sys;
  userInput: IUserInput;
  monthlyVolumeSliderValue: SpringValue<number>;
  averagePaymentSizeSliderValue: SpringValue<number>;
  achAdoptionSliderValue: SpringValue<number>;
}

export const ACHForm: React.FC<ACHFormProps> = ({
  achAdoptionInputRef,
  achAdoptionSliderRef,
  averagePaymentSizeInputRef,
  averagePaymentSizeSliderRef,
  monthlyVolumeSliderValue,
  averagePaymentSizeSliderValue,
  achAdoptionSliderValue,
  fields,
  formRef,
  handleChange,
  hasUserInteracted,
  monthlyVolumeInputRef,
  monthlyVolumeSliderRef,
  sys,
  userInput,
}) => {
  return (
    <Stack
      gap={{ xs: 1.5, md: 2 }}
      justifyContent='center'
      sx={{ height: '100%', width: '100%' }}
    >
      <RichTextSwitch
        sys={sys}
        content={fields?.formBody}
        nodes={{
          h1: {
            variant: 'h3',
            component: 'h1',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '2.4rem', sm: '4.0rem' },
            },
          },
          h2: {
            variant: 'h3',
            component: 'h2',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '2.4rem', sm: '4.0rem' },
            },
          },
          p: {
            variant: 'p',
            component: 'p',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '1.8rem', sm: '2.0rem' },
            },
          },
        }}
      />
      <Form ref={formRef}>
        <Label as='span' sx={{ mb: '-1.2rem' }}>
          Monthly transaction volume
        </Label>
        <Stack direction='row' gap={3.75} alignItems={'center'}>
          <Label htmlFor='monthly-transaction-volume'>
            <VisuallyHiddenContainer>
              Monthly transaction volume
            </VisuallyHiddenContainer>
            <input
              ref={monthlyVolumeInputRef}
              id='monthly-transaction-volume'
              name='monthlyVolume'
              onChange={handleChange}
              type='text'
              inputMode='numeric'
              value={formatCurrency({
                value: userInput?.monthlyVolume,
                showDecimals: false,
              })}
            />
          </Label>
          <Slider
            tabIndex={-1}
            isPulsing={!hasUserInteracted.current}
            max='250000'
            min='0'
            name='monthlyVolume'
            onChange={handleChange}
            onFocus={() => {
              monthlyVolumeInputRef.current?.focus();
            }}
            onMouseUp={() => {
              monthlyVolumeInputRef.current?.blur();
              monthlyVolumeSliderRef.current?.blur();
            }}
            ref={monthlyVolumeSliderRef}
            type='range'
            value={
              hasUserInteracted.current
                ? userInput.monthlyVolume
                : monthlyVolumeSliderValue
            }
          />
        </Stack>
        <Label as='span' sx={{ mb: '-1.2rem' }}>
          Average transaction amount
        </Label>
        <Stack direction='row' gap={3.75} alignItems={'center'}>
          <Label htmlFor='average-transaction-amount'>
            <VisuallyHiddenContainer>
              Average transaction amount
            </VisuallyHiddenContainer>
            <input
              ref={averagePaymentSizeInputRef}
              id='average-transaction-amount'
              name='averagePaymentSize'
              onChange={handleChange}
              type='text'
              inputMode='numeric'
              value={formatCurrency({
                value: userInput?.averagePaymentSize,
                showDecimals: false,
              })}
            />
          </Label>
          <Slider
            tabIndex={-1}
            max='1000'
            min='0'
            name='averagePaymentSize'
            onChange={handleChange}
            type='range'
            ref={averagePaymentSizeSliderRef}
            value={
              hasUserInteracted.current
                ? userInput.averagePaymentSize
                : averagePaymentSizeSliderValue
            }
            onFocus={() => {
              averagePaymentSizeInputRef.current?.focus();
            }}
            onMouseUp={() => {
              averagePaymentSizeInputRef.current?.blur();
              averagePaymentSizeSliderRef.current?.blur();
            }}
          />
        </Stack>
        <Label as='span' sx={{ mb: '-1.2rem' }}>
          ACH adoption
        </Label>
        <Stack direction='row' gap={3.75} alignItems={'center'}>
          <Label htmlFor='ach-adoption'>
            <VisuallyHiddenContainer>ACH adoption</VisuallyHiddenContainer>
            <input
              ref={achAdoptionInputRef}
              id='ach-adoption'
              name='achAdoption'
              onChange={handleChange}
              type='text'
              inputMode='numeric'
              value={`${userInput?.achAdoption}%`}
            />
          </Label>
          <Slider
            tabIndex={-1}
            max='100'
            min='0'
            name='achAdoption'
            onChange={handleChange}
            type='range'
            value={
              hasUserInteracted.current
                ? userInput.achAdoption
                : achAdoptionSliderValue
            }
            ref={achAdoptionSliderRef}
            onFocus={() => {
              achAdoptionInputRef.current?.focus();
            }}
            onMouseUp={() => {
              achAdoptionInputRef.current?.blur();
              achAdoptionSliderRef.current?.blur();
            }}
          />
        </Stack>
      </Form>
    </Stack>
  );
};
